import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import * as Sentry from '@sentry/react'
import { tf, type TfKey } from '../copyTaste'
import { lf } from '../copyLaneF'
import { answered, skip, startCheckin, useCheckin, type CheckOuting } from '../lib/checkin'
import { PAD_AXES, padBands, padPoint, type PadAxis } from '../lib/checkinCore'
import { signal } from '../lib/tasteFeedback'
import { buttonClass } from '../ui/styles'

/** Fable review: the check-in ownership gate answers 403 for an outing that is not this person's: drop it, never retry forever. */
const notMine = (e: unknown) => (e as { status?: number } | null)?.status === 403
import { Spinner } from '../ui/Spinner'
import { Check } from '../ui/icons'
import { FADE } from '../ui/motion'

type Step = { kind: 'ask' } | { kind: 'axis' } | { kind: 'pad'; axis: PadAxis } | { kind: 'thanks'; line: TfKey }


/**
 * "Did you go?" (R-14 app half, Alvin 10/08): one card at the top of Browse, the next time the person opens the app after
 * outings whose time has passed. Several waiting = one card that says how many and asks one at a time. Yes opens the pad;
 * No sends didnt_go (not a dislike); Skip asks again on a later open (at most twice). One surface per open: lib/checkin.ts.
 */
export function CheckInCard() {
  const { queue } = useCheckin()
  useEffect(() => { void startCheckin() }, [])
  const [step, setStep] = useState<Step>({ kind: 'ask' })
  const [done, setDone] = useState(false) // every waiting outing answered: the closing line, then the card leaves
  const [gone, setGone] = useState(false)
  const [err, setErr] = useState(false)
  const reduce = useReducedMotion() ?? false
  const cur = queue?.[0] ?? null
  useEffect(() => {
    if (step.kind !== 'thanks') return
    const t = window.setTimeout(() => { setStep({ kind: 'ask' }); if (!queue?.length) setDone(true) }, 1400)
    return () => window.clearTimeout(t)
  }, [step, queue])
  useEffect(() => {
    if (!done) return
    const t = window.setTimeout(() => setGone(true), 2200)
    return () => window.clearTimeout(t)
  }, [done])

  if (!queue || gone) return null
  if (!cur && step.kind !== 'thanks' && !done) return null
  const title = cur?.item.title ?? ''
  const left = queue.length

  const yes = async () => {
    if (!cur) return
    setErr(false)
    setStep({ kind: 'axis' })
    try {
      const r = await signal({ pad_axis: cur.item.id })
      const axis = (PAD_AXES as readonly string[]).includes(r.y_axis ?? '') ? (r.y_axis as PadAxis) : 'connection'
      setStep({ kind: 'pad', axis })
    } catch (e) {
      if (notMine(e)) { answered(cur.item.id); setStep({ kind: 'ask' }); return }
      Sentry.captureException(e)
      setErr(true)
      setStep({ kind: 'ask' })
    }
  }
  const no = async () => {
    if (!cur) return
    setErr(false)
    try {
      await signal({ kind: 'didnt_go', item: cur.item.id, source: 'checkin' })
      answered(cur.item.id)
      setStep({ kind: 'thanks', line: 'tf.ci_no_thanks' })
    } catch (e) {
      if (notMine(e)) { answered(cur.item.id); return }
      Sentry.captureException(e)
      setErr(true)
    }
  }

  return (
    <motion.section data-review-id="checkin-card" aria-labelledby="checkin-q" layout={!reduce}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={FADE.enter}
      className="mt-5 border border-line bg-surface p-5">
      {done && !cur ? (
        <p role="status" className="t-control flex items-center gap-2 text-ink"><Check size={18} strokeWidth={2.5} className="text-accent" />{tf('tf.ci_done')}</p>
      ) : step.kind === 'thanks' ? (
        <p role="status" className="t-control flex items-center gap-2 text-ink"><Check size={18} strokeWidth={2.5} className="text-accent" />{tf(step.line)}</p>
      ) : cur && (
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={`${cur.item.id}:${step.kind}`} {...(reduce ? {} : { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } })} transition={FADE.enter}>
            <p data-review-id="checkin-count" className="t-label text-ink-2">{left === 1 ? tf('tf.ci_count_one') : tf('tf.ci_count_other', { count: left })}</p>
            {step.kind === 'pad' ? (
              <Pad outing={cur} axis={step.axis} onSent={() => { answered(cur.item.id); setStep({ kind: 'thanks', line: 'tf.pad_thanks' }) }} onGone={() => { answered(cur.item.id); setStep({ kind: 'ask' }) }} />
            ) : (
              <>
                <h2 id="checkin-q" data-sentry-mask className="t-headline mt-2 text-ink [overflow-wrap:anywhere]">{tf('tf.ci_question', { title })}</h2>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button type="button" data-review-id="checkin-yes" disabled={step.kind === 'axis'} onClick={() => void yes()} className={buttonClass('primary', 'md', 'w-full')}>
                    {step.kind === 'axis' && <Spinner size={16} label={false} className="text-current" />}{tf('tf.ci_yes')}
                  </button>
                  <button type="button" data-review-id="checkin-no" disabled={step.kind === 'axis'} onClick={() => void no()} className={buttonClass('secondary', 'md', 'w-full')}>{tf('tf.ci_no')}</button>
                </div>
                <button type="button" data-review-id="checkin-skip" onClick={() => { skip(cur.item.id); if (queue.length <= 1) setGone(true) }}
                  className="press mt-1 inline-flex min-h-11 items-center text-[15px] font-semibold text-ink-2 underline underline-offset-4">{tf('tf.ci_skip')}</button>
                {err && <p role="alert" className="t-meta mt-1 text-error">{tf('tf.error')}</p>}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      )}
    </motion.section>
  )
}

const KEY_STEP = 20

/**
 * The feedback pad (RECS-SYSTEM 3.12): one tap on a square. X = didn't like it <-> loved it, always. Y = the question the
 * server picked for this outing and this person (`signal { pad_axis }`), its words above the square and at its top and
 * bottom edges. The dashed middle is "not sure". A tap or a drag places the dot and reads it back; another tap or drag moves it; Send sends.
 */
export function Pad({ outing, axis, onSent, onGone = () => {}, preview = false, start = null, sendWith }: {
  outing: CheckOuting; axis: PadAxis; onSent: () => void; onGone?: () => void
  /** QA panel (features 10/16): Send sends nothing; `start` places the dot in a chosen quadrant */
  preview?: boolean; start?: { x: number; y: number } | null
  /** Round 3 "How was it?" link (screens/HowWasIt.tsx): the answer goes with the signed link instead of this session */
  sendWith?: (p: { x: number; y: number; y_axis: PadAxis }) => Promise<void>
}) {
  const [pt, setPt] = useState<{ x: number; y: number } | null>(start)
  const [sending, setSending] = useState(false)
  const [err, setErr] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  // R-101 (Alvin: "put your thumb and move it around to adjust the final score"): the dot follows the thumb or mouse while
  // it is down (pointer capture keeps the moves coming outside the square; touch-none stops the page scrolling). A tap is a
  // down and an up, so it still places the dot; whatever is shown when Send is tapped is what is sent.
  const dragging = useRef(false)
  const place = (e: PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    setPt(padPoint(e.clientX - r.left, e.clientY - r.top, r.width, r.height))
  }
  const down = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return
    dragging.current = true
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* old browsers: moves inside the square still work */ }
    place(e)
  }
  const move = (e: PointerEvent<HTMLDivElement>) => { if (dragging.current) place(e) }
  const up = (e: PointerEvent<HTMLDivElement>) => {
    dragging.current = false
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* not captured */ }
  }
  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    const d: Record<string, [number, number]> = { ArrowLeft: [-KEY_STEP, 0], ArrowRight: [KEY_STEP, 0], ArrowUp: [0, KEY_STEP], ArrowDown: [0, -KEY_STEP] }
    const m = d[e.key]
    if (!m) return
    e.preventDefault()
    const c = pt ?? { x: 0, y: 0 }
    setPt({ x: Math.max(-100, Math.min(100, c.x + m[0])), y: Math.max(-100, Math.min(100, c.y + m[1])) })
  }
  const bands = pt ? padBands(pt.x, pt.y) : null
  const yWord = (b: 'low' | 'mid' | 'high') => (b === 'high' ? tf(`tf.pad_hi.${axis}` as TfKey) : b === 'low' ? tf(`tf.pad_lo.${axis}` as TfKey) : axis === 'challenge' ? tf('tf.pad_mid.challenge') : tf('tf.pad_mid'))
  const xWord = (b: 'low' | 'mid' | 'high') => (b === 'high' ? tf('tf.pad_x_high') : b === 'low' ? tf('tf.pad_x_low') : tf('tf.pad_mid'))
  const readback = !bands ? null : bands === 'unsure' ? tf('tf.pad_unsure') : `${xWord(bands.x)} · ${yWord(bands.y)}`
  const send = async () => {
    if (!pt) return
    if (preview) { onSent(); return }
    setSending(true)
    setErr(false)
    try {
      if (sendWith) await sendWith({ x: pt.x, y: pt.y, y_axis: axis })
      else await signal({ kind: 'pad', item: outing.item.id, x: pt.x, y: pt.y, y_axis: axis, source: 'checkin' })
      onSent()
    } catch (e) {
      if (notMine(e)) { onGone(); return }
      Sentry.captureException(e)
      setErr(true)
    } finally {
      setSending(false)
    }
  }
  const q = tf(`tf.pad_q.${axis}` as TfKey)
  const pct = (v: number) => `${(v + 100) / 2}%`
  return (
    <div data-review-id="pad" data-axis={axis}>
      <h2 id="checkin-q" data-sentry-mask className="t-headline mt-2 text-ink [overflow-wrap:anywhere]">{tf('tf.pad_title', { title: outing.item.title })}</h2>
      <p data-review-id="pad-question" className="t-body mt-3 font-semibold text-ink">{q}</p>
      <p className="t-meta mt-0.5 text-ink-2">{lf('lf.pad_hint')}</p>
      <div className="mx-auto mt-3 w-full max-w-[320px]">
        <div ref={box} role="slider" tabIndex={0} aria-label={`${tf('tf.pad_x_low')} / ${tf('tf.pad_x_high')}. ${q}`} aria-valuetext={readback ?? lf('lf.pad_hint')}
          aria-valuemin={-100} aria-valuemax={100} aria-valuenow={pt?.x ?? 0}
          data-review-id="pad-square" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onLostPointerCapture={() => { dragging.current = false }} onKeyDown={key}
          className="relative aspect-square w-full cursor-crosshair touch-none select-none rounded-[16px] bg-bg ring-1 ring-inset ring-line focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
          {/* the axes */}
          <span aria-hidden="true" className="absolute inset-x-3 top-1/2 h-px bg-line" />
          <span aria-hidden="true" className="absolute inset-y-3 left-1/2 w-px bg-line" />
          {/* the not-sure middle (the server's dead zone: |x| and |y| under 15) */}
          <span aria-hidden="true" className="absolute top-[42.5%] left-[42.5%] flex size-[15%] items-center justify-center rounded-[6px] border border-dashed border-line" />
          {/* edge captions: Y on top and bottom (this outing's question), X on the left and right (always) */}
          <span aria-hidden="true" data-review-id="pad-y-high" className="absolute top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-bg px-2 text-[13px] font-semibold text-ink">↑ {tf(`tf.pad_hi.${axis}` as TfKey)}</span>
          <span aria-hidden="true" data-review-id="pad-y-low" className="absolute bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-bg px-2 text-[13px] font-semibold text-ink">↓ {tf(`tf.pad_lo.${axis}` as TfKey)}</span>
          <span aria-hidden="true" className="absolute top-1/2 left-2 max-w-[30%] -translate-y-1/2 bg-bg px-1 text-[12px] font-semibold leading-[15px] text-ink-2">← {tf('tf.pad_x_low')}</span>
          <span aria-hidden="true" className="absolute top-1/2 right-2 max-w-[30%] -translate-y-1/2 bg-bg px-1 text-right text-[12px] font-semibold leading-[15px] text-ink-2">{tf('tf.pad_x_high')} →</span>
          {pt && <span aria-hidden="true" data-review-id="pad-dot" className="round absolute size-6 -translate-x-1/2 translate-y-1/2 rounded-full bg-accent ring-4 ring-accent/25" style={{ left: pct(pt.x), bottom: pct(pt.y) }} />}
        </div>
      </div>
      <p aria-live="polite" data-review-id="pad-readback" className="t-control mt-3 min-h-6 text-center text-ink">{readback ?? ''}</p>
      {pt && <p className="t-meta text-center text-ink-2">{lf('lf.pad_adjust')}</p>}
      <button type="button" data-review-id="pad-send" disabled={!pt || sending} onClick={() => void send()} className={buttonClass('primary', 'lg', 'mt-3 w-full')}>
        {sending && <Spinner size={18} label={false} className="text-current" />}{tf('tf.pad_send')}
      </button>
      {err && <p role="alert" className="t-meta mt-1 text-error">{tf('tf.pad_error')}</p>}
    </div>
  )
}
